import { Link, Outlet, useLocation } from "react-router-dom";
import { Badge, Button, Container, Nav, Navbar, Row } from "react-bootstrap";
import { useEffect, useState } from "react";
import { useFilters } from "../Filters/api/getFilters";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { setHideFilter } from "../../redux/filtersSlice";
import { useTranslation } from "react-i18next";
import ModalGlobal from '../AppModal/ModalGlobal';
import AlertMessage from '../AlertMessage';
import { useAlert } from "../../hooks/useAlert";
import FiltersContainer from "../Filters/components/FiltersContainer";
import useExportToPDF from "../../hooks/useExportToPDF";
import { ApiErrorResponse } from "../../types/ApiErrorResponse";
import { addAuditLog } from "../../features/auditLogs/api/auditApi";
import { AuditActionEnum } from "../../features/auditLogs/types";

export type DashboardOutletContext = {
    section1Ref: React.RefObject<HTMLDivElement>;
    section2Ref: React.RefObject<HTMLDivElement>;
    section3Ref: React.RefObject<HTMLDivElement>;
    feelingchartRef: React.RefObject<HTMLDivElement>;
    setShowNavigation: (show: boolean) => void;
};

type mainDashboardTabs = "dashboard" | "sentiment" | "topics" | "historic"; 

export default function GlobalDashboardLayout() {
    const location = useLocation();
    const dispatch = useDispatch();

    const { data: filters } = useFilters();
    const { t } = useTranslation(['common','dashboard']);
    const [showNavigation, setShowNavigation] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const handleOpen = () => setShowModal(true);
    const handleClose = () => setShowModal(false);


    const { showAlert, hideAlert, alert, isVisible } = useAlert({ fromLocation: false });

    const {
        exportToPDF,
        isExporting,
        section1Ref,
        section2Ref,
        section3Ref
    } = useExportToPDF(t, 'dashboard', filters);

    const MENU_ITEMS = [
        { name: "Dashboard", link: "dashboard", notifications: undefined, tab: 'dashboard' as mainDashboardTabs },
        { name: t("dashboard:menuGlobalSentimentAnalysis"), link: "sentiment", notifications: undefined, tab: 'sentiment' as mainDashboardTabs },
        { name: t("dashboard:menuGlobalTopics"), link: "topics", notifications: undefined, tab: 'topics' as mainDashboardTabs },
        { name: t("dashboard:menuGlobalDataHistory"), link: "historic", notifications: undefined, tab: 'historic' as mainDashboardTabs }
    ];

    useEffect(() => {
        const currentPath = location.pathname;
        const matchingItem = MENU_ITEMS.find((item) => currentPath.includes(item.link));
        if (matchingItem) {
            setSelectedItem(matchingItem.link);
        } else {
            setSelectedItem("dashboard");
        }
    }, [location.pathname]);

    const [selectedItem, setSelectedItem] = useState<string>("dashboard");
    const [isFilterVisible, setIsFilterVisible] = useState<boolean>(false)
    const isHideFilter = useSelector((state: RootState) => state.filters.dashboard.hideFilter);
    
    const handleClick = () => {
        dispatch(setHideFilter({ table: 'dashboard', hide: false }));  // Ejecutar el dispatch de la acción
        setIsFilterVisible(!isFilterVisible); // Cambiar el estado local
    };



    const handleExportPdf = async () => {
        try {
            if (section1Ref.current && section2Ref.current ) {
                exportToPDF();
                await addAuditLog({
                    entity: 'Dashboard',
                    action: AuditActionEnum.Print,
                    source: window.location.pathname,
                    description: "Full dashboard report printed"
                });
                handleClose();
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message

            showAlert({
                message: errorMessage?.toString() ?? t("common:modalPrint.errorToGeneratePdf"),
                type: 'danger',
                duration: 8000
            });
        }
    }

    return (
        <Container fluid>
            <ModalGlobal
                show={showModal}
                onClose={handleClose}
                title={t("common:modalPrint.modalShowHistoricalConfirmationPDFTitle")}
                subtitle={t("common:modalPrint.modalShowHistoricalConfirmationPDFDescription", { description: "" })}
                body={<div></div>}
                onSave={() => {
                    handleExportPdf();
                }}
                contentType='info'
                saveText={t("common:modalActionOK")}
                closeText={t("common:modalActionNOTOK")}
                isLoading={false}
            />           
            {showNavigation && (               
                <Navbar bg="white" expand="lg" className="w-100">
                    <Navbar.Toggle aria-controls="basic-navbar-nav" />
                    <Navbar.Collapse id="basic-navbar-nav" className="w-100">
                        <Nav className="me-auto flex-grow-1 flex-wrap" variant="tabs" activeKey={MENU_ITEMS.findIndex(item => item.link === selectedItem)}>
                            {MENU_ITEMS.map((item, index) => (
                                <Nav.Link 
                                    as={Link}
                                    to={`${item.link}`}
                                    key={index}
                                    eventKey={index}
                                    onClick={() => setSelectedItem(item.link)}
                                    className={`position-relative text-nowrap survey-tab-link ${item.link === selectedItem ? "active-tab" : ""}`}
                                >
                                    {item.name}
                                    {!!item.notifications &&
                                        <Badge bg="success" className="position-absolute top-0  translate-middle badge rounded-pill">{item.notifications < 10 ? item.notifications : "+9"}</Badge>
                                    }
                                </Nav.Link>
                            ))}
                        </Nav>
                        <Nav className="ms-auto flex-shrink-0">
                            <Button 
                                variant="" 
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleOpen();
                                }} 
                                aria-controls="collapse-filter-id" 
                                style={{ 
                                    border: 0,
                                    visibility: (selectedItem === "dashboard") ? 'visible': 'hidden' 
                                }}
                            >
                                <img 
                                    src="/download.png"
                                    alt={isExporting ? 'Generant PDF...' : t(" ButtonExportTitle")}
                                    style={{ width: '50px', }}
                                />
                            </Button>
                            
                            <Button 
                                variant="" 
                                onClick={handleClick} 
                                aria-controls="collapse-filter-id" 
                                style={{ 
                                    border: 0,
                                    visibility: selectedItem === "alertes" ? 'hidden' : 'visible'
                                }}
                            >
                                <img
                                    src="/filter.png"
                                    alt="Filtar"
                                    style={{ width: '50px', }}
                                />
                            </Button>
                        </Nav>
                    </Navbar.Collapse>
                </Navbar>
            )}

            <div ref={section3Ref} className="dashboard-section">
                {showNavigation && !isHideFilter && filters && selectedItem != "/alertes" &&
                    (
                        <FiltersContainer
                            filters={filters}
                            isFilterVisible={isFilterVisible}
                            tableKey="dashboard"
                            historicalKey="historicalData"
                        />
                    )
                }
            </div>

            <Row>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <div className="justify-content-center p-0">
                <Outlet context={{ section1Ref, section2Ref, section3Ref, setShowNavigation }} />
            </div>
        </Container>
    );
}


